import React, { useState, useRef, useEffect } from 'react';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridSortModel,
  GridFilterModel,
  GridActionsCellItem,
  GridRowParams,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import { Box, Popover, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Info, Edit, Trash2, Merge } from 'lucide-react';
import { LgmDataTableType } from './LgmDataTable';

export interface CustomAction<T> {
  icon: React.ReactElement;
  onAction: (row: T) => void;
  title?: string;
  style?: React.CSSProperties;
  hoverStyle?: React.CSSProperties;
  showInMenu?: boolean;
}

interface LgmDataGridProps<T extends LgmDataTableType> {
  data: T[];
  columns: GridColDef<T>[];
  loading?: boolean;
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
  filterModel?: GridFilterModel;
  onFilterModelChange?: (model: GridFilterModel) => void;
  onInfo?: (row: T) => void;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  onMerge?: (row: T) => void;
  actions?: (row: T) => CustomAction<T>[];
  getRowId?: (row: T) => string | number;
}

/**
 * Cell component that shows a popover with full content when clicked if truncated
 */
function TruncatedCell({ params }: { params: GridRenderCellParams }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const cellRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cellRef.current) {
      const element = cellRef.current;
      setIsTruncated(element.scrollWidth > element.clientWidth);
    }
  }, [params.value]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isTruncated) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const value = params.value ?? '';

  return (
    <>
      <div
        ref={cellRef}
        onClick={handleClick}
        style={{
          ...cellStyles.truncatedCellContainer,
          cursor: isTruncated ? 'pointer' : 'default',
        }}
        title={isTruncated ? 'Click to view full content' : undefined}>
        {String(value)}
      </div>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            style: cellStyles.popoverPaper,
          },
        }}>
        <Typography
          sx={{
            p: 2,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>
          {String(value)}
        </Typography>
      </Popover>
    </>
  );
}

/**
 * Generic MUI DataGrid wrapper with LGM Navigator features
 * Supports server-side pagination, sorting, and filtering
 * Includes CSV export, column management, and density controls
 */
function LgmDataGrid<T extends LgmDataTableType>({
  data,
  columns,
  loading = false,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  sortModel = [],
  onSortModelChange,
  filterModel,
  onFilterModelChange,
  onInfo,
  onEdit,
  onDelete,
  onMerge,
  actions: customActionsGetter,
  getRowId,
}: LgmDataGridProps<T>) {
  // Add actions column if any action handlers are provided
  const hasActions = Boolean(
    onInfo || onEdit || onDelete || onMerge || customActionsGetter
  );

  // Calculate dynamic width based on number of actions
  const actionCount =
    (onInfo ? 1 : 0) +
    (onEdit ? 1 : 0) +
    (onDelete ? 1 : 0) +
    (onMerge ? 1 : 0);

  // Each icon is ~40px, plus padding. Add estimated custom actions count.
  const estimatedCustomActions = customActionsGetter ? 1 : 0; // Estimate at least 1 if provided
  const totalActions = actionCount + estimatedCustomActions;
  const actionsColumnWidth = Math.max(80, totalActions * 35 + 10); // Min 80px, ~40px per action + 30px padding

  // Enhance columns with TruncatedCell for those without custom renderCell
  const enhancedColumns = columns.map(col => {
    if (!col.renderCell && col.type !== 'actions') {
      return {
        ...col,
        renderCell: (params: GridRenderCellParams) => (
          <TruncatedCell params={params} />
        ),
      };
    }
    return col;
  });

  const actionsColumn: GridColDef<T> = {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: actionsColumnWidth,
    getActions: (params: GridRowParams<T>) => {
      const actions = [];

      if (onInfo) {
        actions.push(
          <GridActionsCellItem
            key="info"
            icon={<Info size={18} />}
            label="Info"
            onClick={() => onInfo(params.row)}
          />
        );
      }

      if (onEdit) {
        actions.push(
          <GridActionsCellItem
            key="edit"
            icon={<Edit size={18} />}
            label="Edit"
            onClick={() => onEdit(params.row)}
          />
        );
      }

      if (onDelete) {
        actions.push(
          <GridActionsCellItem
            key="delete"
            icon={<Trash2 size={18} />}
            label="Delete"
            onClick={() => onDelete(params.row)}
          />
        );
      }

      if (onMerge) {
        actions.push(
          <GridActionsCellItem
            key="merge"
            icon={<Merge size={18} />}
            label="Merge"
            onClick={() => onMerge(params.row)}
          />
        );
      }

      // Add custom actions if provided
      if (customActionsGetter) {
        const customActions = customActionsGetter(params.row);
        customActions.forEach((action, index) => {
          actions.push(
            <GridActionsCellItem
              key={`custom-${index}`}
              icon={action.icon}
              style={action.style}
              label={action.title || `Action ${index + 1}`}
              onClick={() => action.onAction(params.row)}
              showInMenu={action.showInMenu}
            />
          );
        });
      }

      return actions;
    },
  };

  const allColumns = hasActions
    ? [...enhancedColumns, actionsColumn]
    : enhancedColumns;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <DataGrid
        rows={data}
        columns={allColumns}
        getRowId={getRowId}
        loading={loading}
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        slotProps={{
          toolbar: {
            showQuickFilter: false,
          },
        }}
        disableRowSelectionOnClick
        disableMultipleRowSelection
        disableColumnSelector={false}
        autoPageSize={false}
        sx={dataGridStyles}
      />
    </Box>
  );
}

const cellStyles: Record<string, React.CSSProperties> = {
  truncatedCellContainer: {
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  popoverPaper: {
    maxWidth: '600px',
    maxHeight: '400px',
    overflow: 'auto',
  } as React.CSSProperties,
};

const dataGridStyles: SxProps<Theme> = {
  flex: 1,
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f5f5f5',
    fontWeight: 600,
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid #e0e0e0',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: '#f9f9f9',
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '2px solid #e0e0e0',
    backgroundColor: '#fafafa',
  },
  '& .MuiDataGrid-actionsCell': {
    gap: '8px',
    '& .MuiButtonBase-root:nth-of-type(1)': {
      color: '#2563eb',
      '&:hover': {
        color: '#1e40af',
        backgroundColor: 'transparent',
      },
    },
    '& .MuiButtonBase-root:nth-of-type(2)': {
      color: '#d97706',
      '&:hover': {
        color: '#92400e',
        backgroundColor: 'transparent',
      },
    },
    '& .MuiButtonBase-root:nth-of-type(3)': {
      color: '#dc2626',
      '&:hover': {
        color: '#991b1b',
        backgroundColor: 'transparent',
      },
    },
    '& .MuiButtonBase-root:nth-of-type(4)': {
      color: '#8b5cf6',
      '&:hover': {
        color: '#6d28d9',
        backgroundColor: 'transparent',
      },
    },
  },
};

export default LgmDataGrid;
